import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Stack, Tooltip, Typography } from '@mui/material';
import { useAuth } from 'src/context/auth/AuthContext';
import { Creator, creatorName, creatorProfilePath } from 'src/utils/creator';

interface CreatedByProps {
  creator?: Creator | null;
  /** Show the email under the name (table cells). */
  showEmail?: boolean;
}

/**
 * Who created a record. Opens the teacher's profile when the creator is a
 * teacher and the viewer may see teacher profiles. Same look as the ledger's
 * scholar cell.
 */
export default function CreatedBy({ creator, showEmail = false }: CreatedByProps): JSX.Element {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const name = creatorName(creator);
  if (!name) {
    return (
      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem', fontStyle: 'italic' }}>
        Not recorded
      </Typography>
    );
  }

  const profilePath = hasPermission('teachers.view_all') ? creatorProfilePath(creator) : null;

  return (
    <Tooltip title={profilePath ? 'View Teacher Profile' : ''}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        data-testid="created-by"
        onClick={(e) => {
          if (!profilePath) return;
          e.stopPropagation();
          navigate(profilePath);
        }}
        sx={{
          cursor: profilePath ? 'pointer' : 'default',
          width: 'fit-content',
          '&:hover .creator-name': {
            color: profilePath ? 'primary.main' : 'text.primary',
            textDecoration: profilePath ? 'underline' : 'none',
          },
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'rgba(124, 58, 237, 0.12)',
            color: '#7C3AED',
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        >
          {name.charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Typography
            className="creator-name"
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem', lineHeight: 1.25, transition: 'color 0.15s ease' }}
          >
            {name}
          </Typography>
          {showEmail && creator?.email && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 400 }}>
              {creator.email}
            </Typography>
          )}
        </Box>
      </Stack>
    </Tooltip>
  );
}
